import React from 'react'

import styled from 'styled-components'

import Box from 'app/components/design-system-components/Box'
import { CheckboxesList } from 'app/components/design-system-components/list-filters/CheckboxesList'
import { DropdownBodySectionHeader } from 'app/components/design-system-components/list-filters/DropdownBodySectionHeader'
import { FilterDropdown } from 'app/components/design-system-components/list-filters/FilterDropdown'
import { theme } from 'app/match/applicationReportPDF/assets/theme'
import { CategoryTypes } from 'app/sm/helpers'

const FilterContainer = styled(Box)`
  min-width: 250px;
  max-height: 400px;
  overflow-y: auto;
  @media print {
    display: none;
  }
`

const FilterWrapper = styled.div`
  @media print {
    display: none;
  }
`

const PropertyTypeFilterBody = ({
  formBag,
  availableTypes,
  propertyTypeFilter,
  onFilterChange,
}) => {
  const { values, setFieldValue } = formBag
  const currentPropertyTypeFilter = values.propertyTypeFilter || {}

  const handleCheckboxChange = (checked, option) => {
    const { optionMeta } = option

    if (optionMeta.id === 'all') {
      if (checked) {
        const allTypeOptions = availableTypes.map((type) => ({
          option: type,
          optionMeta: {
            id: type,
            displayText: CategoryTypes[type],
          },
        }))

        const newFilter = Object.assign(
          {},
          ...allTypeOptions.map((option) => ({
            [option.optionMeta.id]: option,
          })),
        )

        setFieldValue('propertyTypeFilter', newFilter)
      } else {
        setFieldValue('propertyTypeFilter', {})
      }
      return
    }

    if (checked) {
      const allFilters = {
        ...currentPropertyTypeFilter,
        [optionMeta.id]: option,
      }
      setFieldValue('propertyTypeFilter', allFilters)
    } else {
      const { [optionMeta.id]: uncheckedOption, ...otherOptions } =
        currentPropertyTypeFilter
      setFieldValue('propertyTypeFilter', otherOptions)
    }
  }

  const getFilterOptions = () => {
    const allOption = {
      option: 'all',
      optionMeta: {
        id: 'all',
        displayText: 'All Property Types',
      },
    }

    const typeOptions = availableTypes.map((type) => ({
      option: type,
      optionMeta: {
        id: type,
        displayText: CategoryTypes[type],
      },
    }))

    return [allOption, ...typeOptions]
  }

  const options = getFilterOptions()

  const getCheckedOptions = () => {
    const checkedOptions = { ...currentPropertyTypeFilter }

    const totalTypeOptions = availableTypes.length
    const selectedTypeOptions = Object.keys(currentPropertyTypeFilter).length

    if (selectedTypeOptions === totalTypeOptions) {
      checkedOptions['all'] = {
        option: 'all',
        optionMeta: {
          id: 'all',
          displayText: 'All Property Types',
        },
      }
    }

    return checkedOptions
  }

  return (
    <FilterContainer minWidth="250px" maxHeight="400px" overflowY="auto">
      <DropdownBodySectionHeader
        header="Property Type"
        containerProps={{ mb: theme.space[4] + 'px' }}
      />
      <CheckboxesList
        options={options}
        checkedOptions={getCheckedOptions()}
        onCheckBoxClicked={handleCheckboxChange}
      />
    </FilterContainer>
  )
}

const PropertyFilter = ({ properties, propertyTypeFilter, onFilterChange }) => {
  const availableTypes = Array.from(
    new Set(
      (properties || []).map((p) =>
        typeof p.type === 'number' ? p.type : Number(p.type),
      ),
    ),
  ).filter((type) => Number.isFinite(type) && type > 0 && CategoryTypes[type])

  const isFilterApplied = () => Object.keys(propertyTypeFilter).length > 0

  const PropertyAppliedFilterRenderer = () => {
    const selected = Object.values(propertyTypeFilter)
    const totalTypeOptions = availableTypes.length
    const selectedTypeOptions = Object.keys(propertyTypeFilter).length

    if (totalTypeOptions === selectedTypeOptions) {
      return 'All Property Types'
    }

    return selected.map((opt) => opt.optionMeta.displayText).join(', ')
  }

  const filterConfig = {
    filterValues: { propertyTypeFilter },
    filterTriggerPlaceholder: 'Property Type',
    isApplied: isFilterApplied(),
    AppliedFilterRenderer: () => PropertyAppliedFilterRenderer(),
    setFilterValues: (newValues) => {
      onFilterChange(newValues.propertyTypeFilter || {})
    },
    FilterBody: PropertyTypeFilterBody,
  }

  return (
    <FilterWrapper>
      <FilterDropdown
        filterConfig={filterConfig}
        filterBodyProps={{
          availableTypes,
          propertyTypeFilter,
          onFilterChange,
        }}
        triggerCmpProps={{ height: '40px', mr: 3, sizeVariant: 'regular' }}
      />
    </FilterWrapper>
  )
}

export default PropertyFilter
